"use client";

import { useEffect, useRef, useState } from "react";
import { resolveTherapistVideo } from "~/lib/therapist-video-assets";
import { cn } from "~/lib/utils";

interface TherapistVideoPreviewProps {
  src: string;
  className?: string;
  fit?: "contain" | "cover";
}

/** No source exists until pointer intent. Keep the loaded clip for later hovers. */
export function TherapistVideoPreview({
  src,
  className,
  fit = "contain",
}: TherapistVideoPreviewProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const attempt = useRef(0);
  const hovering = useRef(false);
  const [state, setState] = useState<"portrait" | "loading" | "playing">(
    "portrait",
  );

  useEffect(() => {
    const container = containerRef.current;
    const video = videoRef.current;
    if (!container || !video) return;
    const motion = window.matchMedia("(prefers-reduced-motion: reduce)");
    const pointer = window.matchMedia("(hover: hover)");
    const stop = () => {
      hovering.current = false;
      attempt.current++;
      video.pause();
      if (video.readyState) video.currentTime = 0;
      setState("portrait");
    };
    const start = async () => {
      if (!pointer.matches || motion.matches || hovering.current) return;
      hovering.current = true;
      const current = ++attempt.current;
      setState("loading");
      try {
        if (!video.hasAttribute("src")) {
          const resolved = await resolveTherapistVideo(src, "preview");
          if (current !== attempt.current) return;
          video.src = resolved;
        }
        video.muted = true;
        await video.play();
      } catch {
        if (current === attempt.current) setState("portrait");
      }
    };
    const enter = () => {
      void start();
    };
    container.addEventListener("mouseenter", enter);
    container.addEventListener("mouseleave", stop);
    motion.addEventListener("change", stop);
    pointer.addEventListener("change", stop);
    // Hydration may finish after the pointer entered the server-rendered portrait.
    if (container.matches(":hover")) void start();
    return () => {
      container.removeEventListener("mouseenter", enter);
      container.removeEventListener("mouseleave", stop);
      motion.removeEventListener("change", stop);
      pointer.removeEventListener("change", stop);
      stop();
      video.removeAttribute("src");
      video.load();
    };
  }, [src]);

  return (
    <div
      ref={containerRef}
      aria-hidden="true"
      className={cn("absolute inset-0 overflow-hidden", className)}
    >
      <video
        ref={videoRef}
        className={cn(
          "h-full w-full opacity-0 transition-opacity duration-300",
          fit === "cover" ? "object-cover" : "object-contain",
          state === "playing" && "bg-primary opacity-100",
        )}
        onPlaying={() => {
          if (hovering.current) setState("playing");
          else videoRef.current?.pause();
        }}
        onWaiting={() => setState(hovering.current ? "loading" : "portrait")}
        onError={() => setState("portrait")}
        onPause={() => setState("portrait")}
        onEmptied={() => setState("portrait")}
        loop
        muted
        playsInline
        preload="none"
      />
      {state === "loading" && (
        <span className="absolute top-2 right-2 size-5 animate-spin rounded-full border-2 border-white/40 border-t-white motion-reduce:animate-none" />
      )}
    </div>
  );
}
